import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Theme system for LINK.
 *
 *   preference  →  what the user picked
 *                  · "system" (default) — follow OS prefers-color-scheme
 *                  · "light" / "dark"   — manual override, persisted
 *
 *   theme       →  resolved value applied to <html data-theme="…">
 *                  always "light" or "dark"
 *
 * The `<html>` element's data-theme attribute is set as early as possible by
 * an inline script in public/index.html (so React boots without a flicker).
 * This context stays in sync and lets components react.
 */
const ThemeContext = createContext({
  theme: "light",
  preference: "system",
  setPreference: () => {},
  toggle: () => {},
});

const STORAGE_KEY = "link.theme";

function readPreference() {
  if (typeof window === "undefined") return "system";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch (_) {}
  return "system";
}

function systemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyDocumentTheme(t) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", t);
  // Also update meta theme-color so iOS/Android status bar matches.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t === "dark" ? "#0f172a" : "#F9F7F4");
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readPreference);
  const [theme, setTheme] = useState(() => {
    const p = readPreference();
    return p === "system" ? systemTheme() : p;
  });

  // React to manual preference changes
  useEffect(() => {
    const next = preference === "system" ? systemTheme() : preference;
    setTheme(next);
    applyDocumentTheme(next);
  }, [preference]);

  // Watch the OS for changes when the user is on "system"
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (preference !== "system") return;
      const next = mq.matches ? "dark" : "light";
      setTheme(next);
      applyDocumentTheme(next);
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener?.(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener?.(onChange);
    };
  }, [preference]);

  const setPreference = useCallback((p) => {
    setPreferenceState(p);
    try { localStorage.setItem(STORAGE_KEY, p); } catch (_) {}
  }, []);

  const toggle = useCallback(() => {
    // Quick toggle: cycles light ↔ dark, leaving "system" if user was on it.
    setPreference(theme === "dark" ? "light" : "dark");
  }, [theme, setPreference]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
